/** @jsx h */
import { type ComponentChildren, createContext, h } from "preact";
import { useContext } from "preact/hooks";
import { type Selectable, useSelect } from "@/react.observable.tsx";

export type SelectableState<D> = D extends Selectable<infer S> ? S : never;

export interface DispatchContextProviderProps<D> {
  value: D;
  children: ComponentChildren;
}

export function createDispatchContext<D>(name: string) {
  const context = createContext<D | null>(null);

  const Provider = (props: DispatchContextProviderProps<D>) => (
    <context.Provider value={props.value}>{props.children}</context.Provider>
  );

  const useDispatcher = (): D => {
    const dispatcher = useContext(context);
    if (!dispatcher) throw new Error(`${name}: no dispatcher provided`);
    return dispatcher;
  };

  const useSelector = <T,>(
    selector: (state: SelectableState<D>) => T,
    isEqual?: (a: T, b: T) => boolean,
  ): T => useSelect(useDispatcher() as Selectable<SelectableState<D>>, selector, isEqual);

  return { context, Provider, useDispatcher, useSelector };
}
